import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/DashboardLayout";
import { ScenarioPassageMedia } from "@/components/ScenarioPassageMedia";
import { KCard } from "@/components/KCard";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/auth/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  getCourses,
  getTeachingCourses,
  getCourseChapters,
  type Course,
} from "@/api/lms/courses";
import {
  type Exam,
  type ExamQuestion,
  type QuestionScenario,
  QuestionTypeBackend,
  addExamQuestion,
  createExam,
  createExamScenario,
  deleteExamScenario,
  deleteExamQuestion,
  getCourseExams,
  getExamQuestions,
  getExamScenarios,
  patchExamQuestion,
  patchExamScenario,
  updateExam,
  regradeCourseExamAttempts,
} from "@/api/lms/exams";
import { type RegradeResult } from "@/api/lms/mock-tests";
import { uploadToS3 } from "@/api/s3";
import { cn } from "@/lib/utils";
import {
  createEmptyMcqOptions,
  parseMcqOptionsFromBackend,
  serializeMcqOptionsForApi,
  newMcqOptionId,
  type McqOptionRow,
  mcqDisplayBadge,
} from "@/lib/mcqOptionIds";
import { renderFurigana, TextFormattingToolbar, insertFormatting } from "@/lib/japaneseHelpers";
import { Plus, Trash2, Save, Send, Loader2, FileText, ArrowUp, ArrowDown, ChevronDown, ChevronUp, Edit3, HelpCircle, Clock, Eye, AudioLines, Image as ImageIcon, Copy, ChevronRight, Volume2, Sparkles, BookOpen, Music, Play, Pause, CheckCircle2, GripVertical, AlertCircle, Languages, Layers, Settings } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

type UiExamType = "CHAPTER" | "FINAL";

const SettingRow = ({ title, description, children, border = true }: { title: string, description: string, children: React.ReactNode, border?: boolean }) => (
  <div className={`flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6 py-5 ${border ? 'border-b border-border/40' : ''}`}>
    <div className="sm:w-1/3 shrink-0 space-y-1">
      <h4 className="text-sm font-semibold text-foreground">{title}</h4>
      <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
    </div>
    <div className="sm:w-2/3 flex-1 flex flex-col justify-center">
      {children}
    </div>
  </div>
);
type ExamCreationStep = "chooseKind" | "chapterEditor";

interface UiQuestion {
  id: string;
  questionText: string;
  options: McqOptionRow[];
  correctId: string;
  marks: number;
  scenarioId: string;
  imageUrl: string;
  audioUrl: string;
  sortOrder: number;
  // Metadata fields
  examCategory?: "VOCABULARY" | "GRAMMAR" | "READING" | "LISTENING";
  estimatedTime?: number;
  difficultyLevel?: "EASY" | "MEDIUM" | "HARD";
  optionType?: "TEXT" | "IMAGE" | "MIXED";
  answerExplanation?: string;
}

type UnifiedFlowItem =
  | {
    type: "scenario";
    id: string;
    sortOrder: number;
    scenario: QuestionScenario;
  }
  | {
    type: "existing-standalone";
    id: string;
    sortOrder: number;
    question: UiQuestion;
  }
  | {
    type: "draft-standalone";
    id: string;
    sortOrder: number;
    question: UiQuestion;
  };

function emptyUiQuestion(scenarioId = ""): UiQuestion {
  const options = createEmptyMcqOptions();
  return {
    id: crypto.randomUUID(),
    questionText: "",
    options,
    correctId: options[0]!.id,
    marks: 1,
    scenarioId,
    imageUrl: "",
    audioUrl: "",
    sortOrder: 0,
    examCategory: "VOCABULARY",
    estimatedTime: 60,
    difficultyLevel: "MEDIUM",
    optionType: "TEXT",
  };
}

function mapBackendToUiQuestion(q: ExamQuestion): UiQuestion {
  const optsRaw = (q.options as { options?: unknown })?.options;
  const options = parseMcqOptionsFromBackend(optsRaw ?? []);

  // Parse embedded metadata from JSON column
  const meta = (q.options as { metadata?: any })?.metadata || {};

  const correctId =
    options.find((o) => o.text?.trim() === q.correctAnswer?.trim())?.id ?? options[0]?.id ?? "";

  return {
    id: q.id,
    questionText: q.questionText,
    options,
    correctId,
    marks: q.marks ?? 1,
    scenarioId: q.scenarioId ?? "",
    imageUrl: q.imageUrl ?? "",
    audioUrl: q.audioUrl ?? "",
    sortOrder: q.sortOrder ?? 0,
    examCategory: meta.examCategory,
    estimatedTime: meta.estimatedTime,
    difficultyLevel: meta.difficultyLevel,
    optionType: meta.optionType || "TEXT",
    answerExplanation: meta.answerExplanation ?? q.answerExplanation ?? "",
  };
}

const CIRCLE_BADGES = ["①", "②", "③", "④", "⑤", "⑥", "⑦", "⑧", "⑨", "⑩"];
function getCircleBadge(index: number) {
  return CIRCLE_BADGES[index] || String(index + 1);
}

export default function CreateExamPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const isTeacherOrAdmin = useMemo(() => {
    return user?.role === "teacher" || user?.role === "admin";
  }, [user?.role]);

  const [courseId, setCourseId] = useState<string>(() => searchParams.get("courseId") ?? "");
  const [chapterId, setChapterId] = useState<string>(() => searchParams.get("chapterId") ?? "");
  const [examType, setExamType] = useState<UiExamType>(() => {
    const typeParam = searchParams.get("type");
    if (typeParam === "FINAL") return "FINAL";
    return "CHAPTER";
  });
  const [creationStep, setCreationStep] = useState<ExamCreationStep>(() => {
    const hasCourse = Boolean(searchParams.get("courseId"));
    const hasChapter = Boolean(searchParams.get("chapterId"));
    return hasCourse && hasChapter ? "chapterEditor" : "chooseKind";
  });

  const [duration, setDuration] = useState<number>(30);
  const [passMarks, setPassMarks] = useState<number>(60);
  const [maxAttempts, setMaxAttempts] = useState<number>(3);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [hasManuallyEditedTitle, setHasManuallyEditedTitle] = useState(false);

  const [hasAutoCreated, setHasAutoCreated] = useState(false);
  const [isAutoCreating, setIsAutoCreating] = useState(false);
  const [autoCreateFailed, setAutoCreateFailed] = useState(false);
  const autoCreateAttemptedRef = useRef(false);

  const hasUrlContext = useMemo(() => {
    return Boolean(searchParams.get("courseId")) && Boolean(searchParams.get("chapterId"));
  }, [searchParams]);

  const [draftQuestions, setDraftQuestions] = useState<UiQuestion[]>([]);
  const [existingEditUi, setExistingEditUi] = useState<UiQuestion[]>([]);

  // Selected active item state
  const [activeItemId, setActiveItemId] = useState<string | null>(null);

  const [editingScenarioId, setEditingScenarioId] = useState<string | null>(null);
  const [editingScenarioText, setEditingScenarioText] = useState("");

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [collapsedScenarios, setCollapsedScenarios] = useState<Record<string, boolean>>({});

  // Re-grade dialog state
  const [showRegradeDialog, setShowRegradeDialog] = useState(false);
  const [regradePreview, setRegradePreview] = useState<RegradeResult | null>(null);
  const [regradeLoading, setRegradeLoading] = useState(false);
  const [regradeApplying, setRegradeApplying] = useState(false);

  const questionStemRef = useRef<HTMLTextAreaElement>(null);
  const scenarioPassageRef = useRef<HTMLTextAreaElement>(null);

  const createExamMutation = useMutation({
    mutationFn: (data: any) => createExam(courseId, data),
    onSuccess: (created) => {
      toast.success("Exam placeholder created");
      queryClient.setQueryData(["courseExams", courseId], (oldData: any) => {
        const items = oldData?.items ?? (Array.isArray(oldData) ? oldData : []);
        if (items.some((item: any) => item.id === created.id)) return oldData;
        return { ...oldData, items: [...items, created] };
      });
      queryClient.invalidateQueries({ queryKey: ["courseExams", courseId] });
      setDraftQuestions([]);
    },
    onError: (err: any) => toast.error(err?.message || "Failed to create exam"),
  });

  const { data: courses = [], isLoading: coursesLoading } = useQuery({
    queryKey: ["adminCourses", user?.id],
    queryFn: async () => {
      const canSeeAllCourses =
        user?.permissions?.create_exam === true ||
        user?.permissions?.edit_course === true;

      const list = canSeeAllCourses
        ? await getCourses({ page: 1, limit: 100 })
        : user?.backendRole === "TEACHER"
          ? await getTeachingCourses()
          : await getCourses({ page: 1, limit: 100 });

      return ((list as any).items ? (list as any).items : (list as any)) as Course[];
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (courses.length > 0 && !courseId) {
      setCourseId(courses[0].id);
    }
  }, [courses, courseId]);

  const { data: chapters = [], isLoading: chaptersLoading } = useQuery({
    queryKey: ["courseChapters", courseId],
    queryFn: () => getCourseChapters(courseId),
    enabled: !!courseId,
  });

  useEffect(() => {
    if (chapters.length > 0 && !chapterId) {
      setChapterId(chapters[0].id);
    }
  }, [chapters, chapterId]);

  const activeCourse = useMemo(() => courses.find((c) => c.id === courseId), [courses, courseId]);
  const activeChapter = useMemo(() => chapters.find((ch) => ch.id === chapterId), [chapters, chapterId]);

  const handleBackToStructure = () => {
    const isTeacher = window.location.pathname.startsWith("/teacher");
    if (isTeacher) {
      navigate(`/teacher/courses/${courseId}/structure`);
    } else {
      navigate(`/admin/courses/${courseId}/structure`);
    }
  };

  const { data: examsData, isLoading: examsLoading, error: examsError } = useQuery({
    queryKey: ["courseExams", courseId],
    queryFn: () => getCourseExams(courseId, { page: 1, limit: 100 }),
    enabled: !!courseId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 1000 * 60 * 10,
  });

  const exams = useMemo(() => {
    return (examsData as any)?.items ?? (Array.isArray(examsData) ? examsData : []);
  }, [examsData]);

  const existingExam = useMemo(() => {
    return (exams as Exam[]).find(x => {
      if (x.type !== examType) return false;
      if (examType === "CHAPTER") return (x.chapterId ?? null) === (chapterId || null);
      return x.chapterId == null;
    }) || null;
  }, [exams, examType, chapterId]);

  const { data: existingQuestions = [], isLoading: questionsLoading } = useQuery({
    queryKey: ["examQuestions", courseId, existingExam?.id],
    queryFn: () => getExamQuestions(courseId, existingExam?.id ?? ""),
    enabled: !!courseId && !!existingExam?.id,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 1000 * 60 * 10,
  });

  const { data: examScenarios = [], isLoading: scenariosLoading } = useQuery({
    queryKey: ["examScenarios", courseId, existingExam?.id],
    queryFn: () => getExamScenarios(courseId, existingExam?.id ?? ""),
    enabled: !!courseId && !!existingExam?.id,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 1000 * 60 * 10,
  });

  const unifiedFlow = useMemo(() => {
    const list: UnifiedFlowItem[] = [];

    for (const s of examScenarios) {
      list.push({
        type: "scenario",
        id: s.id,
        sortOrder: s.sortOrder ?? 0,
        scenario: s,
      });
    }

    const existingStandalone = existingEditUi.filter((q) => !q.scenarioId);
    for (const q of existingStandalone) {
      list.push({
        type: "existing-standalone",
        id: q.id,
        sortOrder: q.sortOrder ?? 0,
        question: q,
      });
    }

    const draftStandalone = draftQuestions.filter((q) => !q.scenarioId);
    for (const q of draftStandalone) {
      list.push({
        type: "draft-standalone",
        id: q.id,
        sortOrder: q.sortOrder ?? 0,
        question: q,
      });
    }

    list.sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) {
        return a.sortOrder - b.sortOrder;
      }
      return a.id.localeCompare(b.id);
    });

    return list;
  }, [examScenarios, existingEditUi, draftQuestions]);

  // Auto-select first item
  useEffect(() => {
    if (!activeItemId && unifiedFlow.length > 0) {
      setActiveItemId(unifiedFlow[0].id);
    }
  }, [unifiedFlow, activeItemId]);

  const activeQuestion = useMemo(() => {
    return existingEditUi.find(q => q.id === activeItemId) ||
      draftQuestions.find(q => q.id === activeItemId) ||
      null;
  }, [activeItemId, existingEditUi, draftQuestions]);

  const activeScenario = useMemo(() => {
    return examScenarios.find(s => s.id === activeItemId) || null;
  }, [activeItemId, examScenarios]);

  const totalMarksSum = useMemo(() => {
    const existingSum = existingEditUi.reduce((sum, q) => sum + (q.marks ?? 1), 0);
    const draftSum = draftQuestions.reduce((sum, q) => sum + (q.marks ?? 1), 0);
    return existingSum + draftSum;
  }, [existingEditUi, draftQuestions]);

  function updateActiveQuestion(updater: (prev: UiQuestion) => UiQuestion) {
    if (!activeQuestion) return;
    const isExisting = existingEditUi.some(q => q.id === activeQuestion.id);
    if (isExisting) {
      setExistingEditUi(prev => prev.map(q => q.id === activeQuestion.id ? updater(q) : q));
    } else {
      setDraftQuestions(prev => prev.map(q => q.id === activeQuestion.id ? updater(q) : q));
    }
  }

  function updateAndPersistQuestion(qId: string, updater: (prev: UiQuestion) => UiQuestion, autoPersist: boolean = false) {
    const isExisting = existingEditUi.some(q => q.id === qId);
    if (isExisting) {
      const qToUpdate = existingEditUi.find(q => q.id === qId);
      if (!qToUpdate) return;
      const updated = updater(qToUpdate);
      setExistingEditUi(prev => prev.map(q => q.id === qId ? updated : q));
      if (autoPersist) {
        saveQuestionMutation.mutate(updated);
      }
    } else {
      const qToUpdate = draftQuestions.find(q => q.id === qId);
      if (!qToUpdate) return;
      const updated = updater(qToUpdate);
      setDraftQuestions(prev => prev.map(q => q.id === qId ? updated : q));
    }
  }

  const persistQuestion = (q: UiQuestion) => {
    const isExisting = existingQuestions.some((x) => x.id === q.id);
    if (isExisting) {
      saveQuestionMutation.mutate(q);
    }
  };

  function moveOption(idx: number, direction: "up" | "down") {
    updateActiveQuestion(q => {
      const nextOptions = [...q.options];
      const targetIdx = direction === "up" ? idx - 1 : idx + 1;
      if (targetIdx >= 0 && targetIdx < nextOptions.length) {
        const temp = nextOptions[idx]!;
        nextOptions[idx] = nextOptions[targetIdx]!;
        nextOptions[targetIdx] = temp;
      }
      return { ...q, options: nextOptions };
    });
  }

  function moveSubQuestionOption(qId: string, idx: number, direction: "up" | "down") {
    updateAndPersistQuestion(qId, prev => {
      const nextOptions = [...prev.options];
      const targetIdx = direction === "up" ? idx - 1 : idx + 1;
      if (targetIdx >= 0 && targetIdx < nextOptions.length) {
        const temp = nextOptions[idx]!;
        nextOptions[idx] = nextOptions[targetIdx]!;
        nextOptions[targetIdx] = temp;
      }
      return { ...prev, options: nextOptions };
    }, true);
  }

  function orderedDraftsWithText(): UiQuestion[] {
    const ordered: UiQuestion[] = [];
    for (const s of examScenarios) {
      for (const d of draftQuestions) {
        if (d.questionText.trim().length > 0 && d.scenarioId === s.id) ordered.push(d);
      }
    }
    for (const d of draftQuestions) {
      if (d.questionText.trim().length > 0 && !d.scenarioId) ordered.push(d);
    }
    return ordered;
  }

  const updateExamMutation = useMutation({
    mutationFn: (data: any) => updateExam(courseId, existingExam?.id ?? "", data),
    onSuccess: () => {
      toast.success("Exam settings saved");
      queryClient.invalidateQueries({ queryKey: ["courseExams", courseId] });
    },
    onError: (err: any) => toast.error(err?.message || "Failed to save exam settings"),
  });

  const saveQuestionMutation = useMutation({
    mutationFn: (ui: UiQuestion) => {
      const raw = existingQuestions.find((q) => q.id === ui.id);
      if (!raw) throw new Error("Question not found");

      const isMcq = raw.type === QuestionTypeBackend.mcq;
      const payload: any = {
        type: raw.type,
        questionText: ui.questionText.trim(),
        marks: ui.marks,
        scenarioId: ui.scenarioId ? ui.scenarioId : null,
        imageUrl: ui.imageUrl.trim() ? ui.imageUrl : null,
        audioUrl: ui.audioUrl.trim() ? ui.audioUrl : null,
      };

      const optionType = ui.optionType || "TEXT";
      const metadata: any = {
        examCategory: ui.examCategory,
        estimatedTime: ui.estimatedTime,
        difficultyLevel: ui.difficultyLevel,
        optionType: optionType,
        answerExplanation: ui.answerExplanation?.trim() || null,
      };

      if (isMcq) {
        const finalOptions = ui.options.map((opt, idx) => {
          if (optionType === "IMAGE") {
            return { ...opt, text: mcqDisplayBadge(idx) };
          }
          return opt;
        });

        // Validation by layout type
        if (optionType === "TEXT" && finalOptions.some(o => !o.text.trim())) {
          throw new Error("Please enter text for all options");
        }
        if (optionType === "IMAGE" && finalOptions.some(o => !o.imageUrl.trim())) {
          throw new Error("Please upload image for all options");
        }
        if (optionType === "MIXED" && finalOptions.some(o => !o.text.trim() || !o.imageUrl.trim())) {
          throw new Error("Please complete both text and image for all options");
        }

        const optionTexts = finalOptions.map((o) => o.text.trim()).filter(Boolean);
        if (optionTexts.length < 2) throw new Error("Each question needs at least 2 options");
        const correctOption = finalOptions.find((o) => o.id === ui.correctId);
        const correct = correctOption?.text.trim();
        if (!correct) throw new Error("Select the correct option");

        payload.options = {
          options: serializeMcqOptionsForApi(finalOptions).options,
          metadata,
        };
        payload.correctAnswer = correct;
      } else {
        payload.options = {
          options: [],
          metadata,
        };
      }

      return patchExamQuestion(courseId, existingExam?.id ?? "", ui.id, payload);
    },
    onMutate: (ui) => setSavingQuestionId(ui.id),
    onSuccess: () => {
      toast.success("Question saved");
      queryClient.invalidateQueries({ queryKey: ["examQuestions", courseId, existingExam?.id] });
    },
    onError: (err: any) => toast.error(err?.message || "Failed to save question"),
    onSettled: () => setSavingQuestionId(null),
  });

  const addScenarioMutation = useMutation({
    mutationFn: (text: string) => {
      const nextSortOrder = unifiedFlow.length > 0
        ? Math.max(...unifiedFlow.map(item => item.sortOrder)) + 1
        : 1;
      return createExamScenario(courseId, existingExam?.id ?? "", {
        instructionsText: text.trim() || undefined,
        sortOrder: nextSortOrder,
      });
    },
    onSuccess: (created) => {
      toast.success("Scenario block added");
      queryClient.invalidateQueries({ queryKey: ["examScenarios", courseId, existingExam?.id] });
      setActiveItemId(created.id);
    },
    onError: (err: any) => toast.error(err?.message || "Failed to add scenario"),
  });

  const deleteScenarioMutation = useMutation({
    mutationFn: (scenarioId: string) => deleteExamScenario(courseId, existingExam?.id ?? "", scenarioId),
    onSuccess: () => {
      toast.success("Scenario block deleted");
      setActiveItemId(null);
      queryClient.invalidateQueries({ queryKey: ["examScenarios", courseId, existingExam?.id] });
      queryClient.invalidateQueries({ queryKey: ["examQuestions", courseId, existingExam?.id] });
    },
    onError: (err: any) => toast.error(err?.message || "Failed to delete scenario"),
  });

  const updateScenarioMediaMutation = useMutation({
    mutationFn: ({ scenarioId, data }: { scenarioId: string, data: any }) =>
      patchExamScenario(courseId, existingExam?.id ?? "", scenarioId, data),
    onMutate: ({ scenarioId }) => setScenarioMediaSavingId(scenarioId),
    onSuccess: () => {
      toast.success("Media updated");
      queryClient.invalidateQueries({ queryKey: ["examScenarios", courseId, existingExam?.id] });
    },
    onError: (err: any) => toast.error(err?.message || "Failed to update media"),
    onSettled: () => setScenarioMediaSavingId(null),
  });


  const deleteQuestionMutation = useMutation({
    mutationFn: (qId: string) => deleteExamQuestion(courseId, existingExam?.id ?? "", qId),
    onSuccess: () => {
      toast.success("Question deleted");
      setActiveItemId(null);
      queryClient.invalidateQueries({ queryKey: ["examQuestions", courseId, existingExam?.id] });
    },
    onError: (err: any) => toast.error(err?.message || "Failed to delete question"),
  });

  const handleDuplicateQuestion = (q: UiQuestion) => {
    const nextSortOrder = (q.sortOrder ?? 0) + 1;
    let newCorrectId = q.correctId;
    const options = q.options.map(o => {
      const newId = newMcqOptionId();
      if (o.id === q.correctId) {
        newCorrectId = newId;
      }
      return {
        ...o,
        id: newId,
      };
    });

    const duplicated: UiQuestion = {
      ...q,
      id: crypto.randomUUID(),
      sortOrder: nextSortOrder,
      questionText: q.questionText ? `${q.questionText} (Copy)` : "Duplicated Question",
      options,
      correctId: newCorrectId,
    };

    setDraftQuestions(prev => [...prev, duplicated]);
    setActiveItemId(duplicated.id);
    toast.success("Duplicated as draft. Click Publish to save.");
  };

  const handleDeleteQuestion = async (qId: string, isDraft: boolean) => {
    if (isDraft) {
      setDraftQuestions(prev => prev.filter(x => x.id !== qId));
      if (activeItemId === qId) setActiveItemId(null);
      toast.success("Draft question deleted");
    } else {
      if (window.confirm("Are you sure you want to permanently delete this question from the exam?")) {
        deleteQuestionMutation.mutate(qId);
      }
    }
  };

  const handleMoveExistingQuestion = async (
    qId: string,
    direction: "up" | "down",
    scenarioId: string
  ) => {
    const list = existingEditUi.filter((q) => q.scenarioId === scenarioId);
    const sorted = [...list].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const index = sorted.findIndex((q) => q.id === qId);
    if (index === -1) return;

    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sorted.length) return;

    const q1 = sorted[index]!;
    const q2 = sorted[targetIndex]!;

    let sort1 = q1.sortOrder ?? 0;
    let sort2 = q2.sortOrder ?? 0;

    if (sort1 === sort2) {
      sort1 = index;
      sort2 = targetIndex;
    }

    try {
      await Promise.all([
        patchExamQuestion(courseId, existingExam?.id ?? "", q1.id, { sortOrder: sort2 }),
        patchExamQuestion(courseId, existingExam?.id ?? "", q2.id, { sortOrder: sort1 }),
      ]);
      toast.success("Questions reordered");
      queryClient.invalidateQueries({ queryKey: ["examQuestions", courseId, existingExam?.id] });
    } catch (err: any) {
      toast.error(err?.message || "Failed to reorder questions");
    }
  };

  const handleMoveDraft = (
    qId: string,
    direction: "up" | "down",
    scenarioId: string
  ) => {
    setDraftQuestions((prev) => {
      const filteredIndices = prev
        .map((q, idx) => (q.scenarioId === scenarioId ? idx : -1))
        .filter((idx) => idx !== -1);

      const subList = filteredIndices.map((idx) => prev[idx]!);
      const currentSubIdx = subList.findIndex((q) => q.id === qId);
      if (currentSubIdx === -1) return prev;

      const targetSubIdx = direction === "up" ? currentSubIdx - 1 : currentSubIdx + 1;
      if (targetSubIdx < 0 || targetSubIdx >= subList.length) return prev;

      const realIndex1 = filteredIndices[currentSubIdx]!;
      const realIndex2 = filteredIndices[targetSubIdx]!;

      const next = [...prev];
      const temp = next[realIndex1]!;
      next[realIndex1] = next[realIndex2]!;
      next[realIndex2] = temp;

      const sort1 = next[realIndex1]!.sortOrder;
      const sort2 = next[realIndex2]!.sortOrder;
      next[realIndex1]!.sortOrder = sort2;
      next[realIndex2]!.sortOrder = sort1;

      return next;
    });
  };

  const handleMoveTopLevelItem = async (index: number, direction: "up" | "down") => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= unifiedFlow.length) return;

    const nextFlow = [...unifiedFlow];
    const temp = nextFlow[index]!;
    nextFlow[index] = nextFlow[targetIndex]!;
    nextFlow[targetIndex] = temp;

    const dbPromises: Promise<any>[] = [];
    const updatedDrafts = [...draftQuestions];

    nextFlow.forEach((item, newIdx) => {
      const newSortOrder = newIdx + 1;

      if (item.type === "draft-standalone") {
        const dIdx = updatedDrafts.findIndex(q => q.id === item.id);
        if (dIdx !== -1) {
          updatedDrafts[dIdx] = { ...updatedDrafts[dIdx]!, sortOrder: newSortOrder };
        }
      } else if (item.type === "existing-standalone") {
        if (item.question.sortOrder !== newSortOrder) {
          dbPromises.push(
            patchExamQuestion(courseId, existingExam?.id ?? "", item.id, { sortOrder: newSortOrder })
          );
        }
      } else if (item.type === "scenario") {
        if (item.scenario.sortOrder !== newSortOrder) {
          dbPromises.push(
            patchExamScenario(courseId, existingExam?.id ?? "", item.id, { sortOrder: newSortOrder })
          );
        }
      }
    });

    setDraftQuestions(updatedDrafts);

    if (dbPromises.length > 0) {
      try {
        await Promise.all(dbPromises);
        toast.success("Items reordered");
        queryClient.invalidateQueries({ queryKey: ["examQuestions", courseId, existingExam?.id] });
        queryClient.invalidateQueries({ queryKey: ["examScenarios", courseId, existingExam?.id] });
      } catch (err: any) {
        toast.error(err?.message || "Failed to reorder items");
      }
    } else {
      toast.success("Draft items reordered");
    }
  };

  const publishDraftMutation = useMutation({
    mutationFn: async () => {
      const toAdd = orderedDraftsWithText();
      if (toAdd.length === 0) throw new Error("Add at least one question with text");

      for (const q of toAdd) {
        const optionType = q.optionType || "TEXT";
        const finalOptions = q.options.map((opt, idx) => {
          if (optionType === "IMAGE") {
            return { ...opt, text: mcqDisplayBadge(idx) };
          }
          return opt;
        });

        // Validation by layout type
        if (optionType === "TEXT" && finalOptions.some(o => !o.text.trim())) {
          throw new Error(`Please enter text for all options in Question "${q.questionText}"`);
        }
        if (optionType === "IMAGE" && finalOptions.some(o => !o.imageUrl.trim())) {
          throw new Error(`Please upload image for all options in Question "${q.questionText}"`);
        }
        if (optionType === "MIXED" && finalOptions.some(o => !o.text.trim() || !o.imageUrl.trim())) {
          throw new Error(`Please complete both text and image for all options in Question "${q.questionText}"`);
        }

        const optionTexts = finalOptions.map((o) => o.text.trim()).filter(Boolean);
        if (optionTexts.length < 2) throw new Error("Each question needs at least 2 options");
        const correctOption = finalOptions.find((o) => o.id === q.correctId);
        const correct = correctOption?.text.trim();
        if (!correct) throw new Error("Select the correct option");

        await addExamQuestion(courseId, existingExam?.id ?? "", {
          type: QuestionTypeBackend.mcq,
          questionText: q.questionText.trim(),
          options: {
            options: serializeMcqOptionsForApi(finalOptions).options,
            metadata: {
              examCategory: q.examCategory,
              estimatedTime: q.estimatedTime,
              difficultyLevel: q.difficultyLevel,
              optionType: optionType,
              answerExplanation: q.answerExplanation?.trim() || undefined,
            },
          },
          correctAnswer: correct,
          marks: q.marks,
          scenarioId: q.scenarioId || undefined,
          sortOrder: q.sortOrder,
          imageUrl: q.imageUrl || undefined,
          audioUrl: q.audioUrl || undefined,
        });
      }
    },
    onSuccess: () => {
      toast.success("Questions added to exam");
      setDraftQuestions([]);
      setActiveItemId(null);
      queryClient.invalidateQueries({ queryKey: ["exam", courseId, existingExam?.id] });
      queryClient.invalidateQueries({ queryKey: ["courseExams", courseId] });
      queryClient.invalidateQueries({ queryKey: ["courseChapters", courseId] });
      queryClient.invalidateQueries({ queryKey: ["examQuestions", courseId, existingExam?.id] });
    },
    onError: (err: any) => toast.error(err?.message || "Failed to publish questions"),
  });

  // Dedicated mutation only for toggling publish state — never bundled with settings
  const setPublishedMutation = useMutation({
    mutationFn: (publish: boolean) => updateExam(courseId, existingExam!.id, { isPublished: publish }),
    onSuccess: (_data, publish) => {
      toast.success(publish ? "✅ Exam is now Live • visible to students" : "📝 Exam moved back to Draft");
      queryClient.invalidateQueries({ queryKey: ["exam", courseId, existingExam?.id] });
      queryClient.invalidateQueries({ queryKey: ["courseExams", courseId] });
      queryClient.invalidateQueries({ queryKey: ["courseChapters", courseId] });
    },
    onError: (err: any) => toast.error(err?.message || "Failed to update visibility"),
  });

  const [savingQuestionId, setSavingQuestionId] = useState<string | null>(null);
  const [scenarioMediaSavingId, setScenarioMediaSavingId] = useState<string | null>(null);

  useEffect(() => {
    if (existingExam) {
      setDuration(existingExam.duration ?? 30);
      setPassMarks(existingExam.passMarks ?? 60);
      setMaxAttempts(existingExam.maxAttempts ?? 3);
      setTitle(existingExam.title ?? "");
      setDescription(existingExam.description ?? "");
    }
  }, [existingExam]);

  const prevDeps = useRef({ courseId, chapterId, examType });
  useEffect(() => {
    if (
      prevDeps.current.courseId !== courseId ||
      prevDeps.current.chapterId !== chapterId ||
      prevDeps.current.examType !== examType
    ) {
      setHasAutoCreated(false);
      setAutoCreateFailed(false);
      autoCreateAttemptedRef.current = false;
      prevDeps.current = { courseId, chapterId, examType };
    }
  }, [courseId, chapterId, examType]);

  const defaultTitle = useMemo(() => {
    if (examType === "FINAL") {
      return activeCourse ? `${activeCourse.title} Final Exam` : "Final Exam";
    }
    return activeChapter ? `${activeChapter.title} Exam` : "Chapter Exam";
  }, [examType, activeCourse, activeChapter]);

  useEffect(() => {
    if (!existingExam && !hasManuallyEditedTitle) {
      setTitle(defaultTitle);
    }
  }, [existingExam, defaultTitle, hasManuallyEditedTitle]);

  useEffect(() => {
    if (
      courseId &&
      (examType === "FINAL" || chapterId) &&
      !examsLoading &&
      !existingExam &&
      !autoCreateAttemptedRef.current &&
      creationStep === "chapterEditor"
    ) {
      autoCreateAttemptedRef.current = true;
      setHasAutoCreated(true);
      setIsAutoCreating(true);

      createExamMutation.mutate(
        {
          type: examType,
          chapterId: examType === "CHAPTER" ? chapterId : null,
          duration: 30,
          totalMarks: 0,
          passMarks: 60,
          maxAttempts: 3,
          title: title || defaultTitle,
          description: "",
          isPublished: false,
        },
        {
          onSuccess: async (created) => {
            queryClient.setQueryData(["courseExams", courseId], (oldData: any) => {
              const items = oldData?.items ?? (Array.isArray(oldData) ? oldData : []);
              if (items.some((item: any) => item.id === created.id)) return oldData;
              return { ...oldData, items: [...items, created] };
            });
            await queryClient.invalidateQueries({ queryKey: ["courseExams", courseId] });
            setIsAutoCreating(false);
          },
          onError: () => {
            setAutoCreateFailed(true);
            setIsAutoCreating(false);
          },
        }
      );
    }
  }, [
    courseId,
    chapterId,
    examType,
    examsLoading,
    existingExam,
    hasAutoCreated,
    isAutoCreating,
    createExamMutation,
    title,
    defaultTitle,
    queryClient,
    creationStep,
  ]);

  const isSettingsDirty = useMemo(() => {
    if (!existingExam) return false;
    return (
      title !== (existingExam.title ?? "") ||
      description !== (existingExam.description ?? "") ||
      duration !== (existingExam.duration ?? 30) ||
      passMarks !== (existingExam.passMarks ?? 60) ||
      maxAttempts !== (existingExam.maxAttempts ?? 3)
    );
  }, [existingExam, title, description, duration, passMarks, maxAttempts]);

  // Sync existing questions into UI state
  useEffect(() => {
    if (existingQuestions.length > 0) {
      setExistingEditUi(prev => {
        const prevMap = new Map(prev.map(p => [p.id, p]));
        return existingQuestions.map(serverQ => {
          return prevMap.has(serverQ.id) ? prevMap.get(serverQ.id)! : mapBackendToUiQuestion(serverQ);
        });
      });
    } else {
      setExistingEditUi([]);
    }
  }, [existingQuestions]);

  // Helper to compare questions
  const isQuestionEqual = useCallback((q1: UiQuestion, q2: UiQuestion) => {
    if (q1.id !== q2.id) return false;
    if (q1.questionText !== q2.questionText) return false;
    if (q1.correctId !== q2.correctId) return false;
    if (q1.marks !== q2.marks) return false;
    if (q1.scenarioId !== q2.scenarioId) return false;
    if (q1.imageUrl !== q2.imageUrl) return false;
    if (q1.audioUrl !== q2.audioUrl) return false;
    if (q1.examCategory !== q2.examCategory) return false;
    if (q1.options.length !== q2.options.length) return false;
    for (let i = 0; i < q1.options.length; i++) {
      if (q1.options[i].text !== q2.options[i].text) return false;
    }
    return true;
  }, []);

  const isDirty = useMemo(() => {
    // 1. Check if there are draft questions
    if (draftQuestions.length > 0) return true;

    // 2. Check if settings/meta forms are modified compared to database
    if (isSettingsDirty) return true;

    // 3. Check if any question in existingEditUi has been modified compared to existingQuestions
    if (existingEditUi.length !== existingQuestions.length) return true;
    for (let i = 0; i < existingEditUi.length; i++) {
      const qUi = existingEditUi[i];
      const qClean = existingQuestions.find(q => q.id === qUi.id);
      if (!qClean) return true;
      const qCleanUi = mapBackendToUiQuestion(qClean);
      if (!isQuestionEqual(qUi, qCleanUi)) return true;
    }

    // 4. Check if there is scenario passage being edited
    if (editingScenarioId && editingScenarioText.trim() !== "") {
      const scen = examScenarios.find(s => s.id === editingScenarioId);
      if (scen && editingScenarioText.trim() !== (scen.instructionsText ?? "").trim()) {
        return true;
      }
    }

    return false;
  }, [
    draftQuestions,
    isSettingsDirty,
    existingEditUi,
    existingQuestions,
    editingScenarioId,
    editingScenarioText,
    examScenarios,
    isQuestionEqual
  ]);

  // Unsaved changes beforeunload warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty]);

  // Debounced localStorage autosave
  useEffect(() => {
    if (!courseId) return;

    const draftKey = `exam_editor_draft_${courseId}_${chapterId}_${examType}`;

    if (isDirty) {
      const timer = setTimeout(() => {
        const snapshot = {
          courseId,
          chapterId,
          examType,
          timestamp: Date.now(),
          draftQuestions,
          existingEditUi,
          editingScenarioId,
          editingScenarioText,
          activeItemId,
          duration,
          passMarks,
          maxAttempts,
          title,
          description,
          hasManuallyEditedTitle,
          creationStep,
        };
        localStorage.setItem(draftKey, JSON.stringify(snapshot));
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      localStorage.removeItem(draftKey);
    }
  }, [
    isDirty,
    courseId,
    chapterId,
    examType,
    draftQuestions,
    existingEditUi,
    editingScenarioId,
    editingScenarioText,
    activeItemId,
    duration,
    passMarks,
    maxAttempts,
    title,
    description,
    hasManuallyEditedTitle,
    creationStep,
  ]);

  const [recoveredDraft, setRecoveredDraft] = useState<any>(null);
  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false);

  // Check for unsaved draft on load
  useEffect(() => {
    if (courseId && !questionsLoading && !examsLoading) {
      const draftKey = `exam_editor_draft_${courseId}_${chapterId}_${examType}`;
      const stored = localStorage.getItem(draftKey);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed && parsed.timestamp) {
            setRecoveredDraft(parsed);
            setShowRecoveryDialog(true);
          }
        } catch (err) {
          console.error("Failed to parse editor draft:", err);
        }
      }
    }
  }, [courseId, chapterId, examType, questionsLoading, examsLoading]);

  const handleRecoverDraft = () => {
    if (!recoveredDraft) return;
    setDraftQuestions(recoveredDraft.draftQuestions || []);
    setExistingEditUi(recoveredDraft.existingEditUi || []);
    setEditingScenarioId(recoveredDraft.editingScenarioId || null);
    setEditingScenarioText(recoveredDraft.editingScenarioText || "");
    setActiveItemId(recoveredDraft.activeItemId || null);
    setDuration(recoveredDraft.duration || 30);
    setPassMarks(recoveredDraft.passMarks || 60);
    setMaxAttempts(recoveredDraft.maxAttempts || 3);
    setTitle(recoveredDraft.title || "");
    setDescription(recoveredDraft.description || "");
    setHasManuallyEditedTitle(recoveredDraft.hasManuallyEditedTitle || false);
    setCreationStep(recoveredDraft.creationStep || "chapterEditor");

    setShowRecoveryDialog(false);
    toast.success("Unsaved draft restored successfully!");
  };

  const handleDiscardDraft = () => {
    const draftKey = `exam_editor_draft_${courseId}_${chapterId}_${examType}`;
    localStorage.removeItem(draftKey);
    setRecoveredDraft(null);
    setShowRecoveryDialog(false);
    toast.info("Draft discarded.");
  };

  if (!user) return null;
  if (!isTeacherOrAdmin) {
    return (
      <DashboardLayout>
        <div className="p-10 max-w-xl mx-auto">
          <KCard className="p-6 text-sm text-muted-foreground">
            You are not allowed to manage exam questions.
          </KCard>
        </div>
      </DashboardLayout>
    );

  }
  const isGlobalSaving = updateExamMutation.isPending || saveQuestionMutation.isPending || addScenarioMutation.isPending || deleteScenarioMutation.isPending || updateScenarioMediaMutation.isPending || deleteQuestionMutation.isPending || publishDraftMutation.isPending || setPublishedMutation.isPending;


  const isLoadingData =
    coursesLoading ||
    chaptersLoading ||
    examsLoading ||
    (isAutoCreating || (creationStep === "chapterEditor" && !existingExam && !autoCreateFailed));

  if (isLoadingData) {
    return (
      <DashboardLayout>
        <div className="p-10 flex flex-col justify-center items-center gap-3 text-muted-foreground min-h-[400px]">
          <Loader2 className="animate-spin text-primary" size={32} />
          <p className="text-sm font-medium animate-pulse">
            {isAutoCreating ? "Initializing exam editor..." : "Loading exam data..."}
          </p>
        </div>
      </DashboardLayout>
    );
  }

  if (creationStep === "chooseKind") {
    return (
      <DashboardLayout>
        <div className="p-6 md:p-10 max-w-3xl mx-auto space-y-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Create an exam</h1>
            <p className="text-muted-foreground">
              Build a course exam (chapter or final exam) and add scenarios and questions.
            </p>
          </div>
          <div className="max-w-md">
            <KCard className="p-6 flex flex-col gap-4 border-border hover:border-primary/40 transition-colors">
              <div className="rounded-lg bg-primary/10 w-12 h-12 flex items-center justify-center text-primary">
                <FileText className="size-6" aria-hidden />
              </div>
              <div className="space-y-1">
                <h2 className="font-semibold text-lg text-foreground">Chapter-based exam</h2>
                <p className="text-sm text-muted-foreground">
                  Tie an exam to a course chapter or create a final exam. Add scenarios and questions here.
                </p>
              </div>
              <Button
                className="mt-auto w-full"
                onClick={() => {
                  setExamType("CHAPTER");
                  setCreationStep("chapterEditor");
                }}
              >
                Continue
              </Button>
            </KCard>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="w-full px-4 md:px-5 pt-4 md:pt-5 pb-2 max-w-[1680px] mx-auto flex flex-col gap-2 min-h-[calc(100vh-64px)] xl:h-[calc(100vh-64px)]">
        {/* Header Block */}
        <div className="dash-page-header !mb-0 border-b border-border/40 pb-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {hasUrlContext ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground h-8 px-2 shrink-0"
                onClick={handleBackToStructure}
              >
                ← Back
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground h-8 px-2 shrink-0"
                onClick={() => setCreationStep("chooseKind")}
              >
                ← Change
              </Button>
            )}
            <div className="min-w-0">
              <h1 className="dash-page-title truncate flex items-center gap-2">
                <HelpCircle className="text-primary size-5 shrink-0" />
                {title || "Japanese Chapter Exam Builder"}
              </h1>
              <p className="dash-page-subtitle truncate">
                Configure course barrier checkpoints and design immersive questions using official CBT patterns.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Config / Settings toggle */}
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs gap-1.5"
                >
                  <Settings size={13} />
                  Config
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-2xl overflow-y-auto pt-6 pb-0" side="right">
                <SheetHeader className="mb-6">
                  <SheetTitle>Course Exam Configuration</SheetTitle>
                  <SheetDescription>
                    Configure exam rules, pass marks, and administrative actions.
                  </SheetDescription>
                </SheetHeader>

                <div className="space-y-2">
                  <SettingRow title="Exam Title" description="The public title displayed to students.">
                    <Input
                      className="h-9 text-sm rounded-xl"
                      value={title}
                      onChange={(e) => {
                        setTitle(e.target.value);
                        setHasManuallyEditedTitle(true);
                      }}
                    />
                  </SettingRow>

                  <SettingRow title="Duration" description="Total allowed time in minutes.">
                    <div className="relative max-w-[150px]">
                      <Input
                        className="h-9 text-sm rounded-xl"
                        type="number"
                        min={1}
                        value={duration}
                        onChange={(e) => setDuration(Math.max(1, Number(e.target.value) || 1))}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-medium pointer-events-none">min</span>
                    </div>
                  </SettingRow>

                  <SettingRow title="Overall Pass Mark" description="Minimum score student must achieve overall.">
                    <div className="relative max-w-[150px]">
                      <Input
                        className="h-9 text-sm rounded-xl focus:ring-primary"
                        type="number"
                        min={0}
                        max={totalMarksSum}
                        value={passMarks}
                        onChange={(e) => setPassMarks(Math.max(0, Number(e.target.value) || 0))}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground font-medium pointer-events-none">/ {totalMarksSum}</span>
                    </div>
                  </SettingRow>

                  <SettingRow title="Total Marks" description="Auto-calculated from all questions in this exam.">
                    <div className="h-9 px-4 max-w-[150px] rounded-xl border border-border/80 bg-muted/40 text-sm font-bold text-foreground flex items-center">
                      {totalMarksSum} {totalMarksSum === 1 ? "Mark" : "Marks"}
                    </div>
                  </SettingRow>

                  {isTeacherOrAdmin && (
                    <>
                      <div className="pt-4 pb-2">
                        <h3 className="font-semibold text-lg text-amber-600 dark:text-amber-500">Advanced</h3>
                      </div>

                      <div className="p-5 flex items-center justify-between gap-4 border border-amber-200/60 dark:border-amber-900/40 bg-amber-50/30 dark:bg-amber-950/20 rounded-xl mb-4 mt-2">
                        <div className="space-y-1">
                          <h3 className="font-medium text-sm text-foreground flex items-center gap-1.5">
                            <AlertCircle className="size-4 text-amber-600 dark:text-amber-500" />
                            Re-grade Past Attempts
                          </h3>
                          <p className="text-[13px] text-muted-foreground leading-relaxed">
                            After fixing a wrong answer, re-score all students' submitted results using the current correct answers.
                          </p>
                        </div>
                        <div className="shrink-0">
                          <Button
                          variant="outline"
                          className="h-9 rounded-xl border-amber-400 text-amber-700 hover:bg-amber-50 dark:border-amber-600 dark:text-amber-400 dark:hover:bg-amber-950"
                          onClick={async () => {
                            if (!existingExam) return;
                            setRegradePreview(null);
                            setShowRegradeDialog(true);
                            setRegradeLoading(true);
                            try {
                              const preview = await regradeCourseExamAttempts(courseId, existingExam.id, true);
                              setRegradePreview(preview);
                            } catch (e: any) {
                              toast.error(e?.message || "Failed to load re-grade preview");
                              setShowRegradeDialog(false);
                            } finally {
                              setRegradeLoading(false);
                            }
                          }}
                        >
                          <AlertCircle size={14} className="mr-2" />
                          Re-grade Attempts
                        </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-6 pt-6 border-t border-border flex justify-end items-start box-content sticky bottom-0 bg-card z-10 pb-6 -mx-6 px-6 pt-4">
                  <Button
                    className="h-10 px-8 rounded-xl"
                    disabled={!isSettingsDirty || updateExamMutation.isPending}
                    onClick={() => updateExamMutation.mutate({
                      type: examType,
                      chapterId: examType === "CHAPTER" ? chapterId : null,
                      duration,
                      passMarks,
                      maxAttempts,
                      title,
                      description,
                    })}
                  >
                    {updateExamMutation.isPending ? <Loader2 className="animate-spin size-4 mr-2" /> : <Save size={16} className="mr-2" />}
                    Save Exam Settings
                  </Button>
                </div>
              </SheetContent>
            </Sheet>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="h-8 text-xs gap-1.5" disabled={!existingExam || publishDraftMutation.isPending}>
                  <Plus size={13} />
                  Add Question
                  <ChevronDown size={12} className="opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => {
                  const d = emptyUiQuestion();
                  d.scenarioId = "";
                  const nextSortOrder = unifiedFlow.length > 0
                    ? Math.max(...unifiedFlow.map(item => item.sortOrder)) + 1
                    : 1;
                  d.sortOrder = nextSortOrder;
                  setDraftQuestions(prev => [...prev, d]);
                  setActiveItemId(d.id);
                  toast.success("Added standalone question draft");
                }}>
                  <FileText className="size-4 mr-2 text-muted-foreground" />
                  Add Standalone Question
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => {
                  addScenarioMutation.mutate("");
                }}>
                  <Layers className="size-4 mr-2 text-muted-foreground" />
                  Add Scenario Question Block
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Commit Questions — saves draft questions to database */}
            {draftQuestions.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs gap-1.5 border-amber-400 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-950/30"
                onClick={() => publishDraftMutation.mutate()}
                disabled={!existingExam || publishDraftMutation.isPending}
              >
                {publishDraftMutation.isPending
                  ? <Loader2 className="animate-spin size-3.5" />
                  : <Save size={13} />
                }
                Commit {draftQuestions.length > 0 && `(${draftQuestions.length})`}
              </Button>
            )}

            {/* Publish / Unpublish — controls student visibility */}
            {existingExam?.isPublished ? (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs gap-1.5 border-emerald-300 text-emerald-700 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 dark:border-emerald-700 dark:text-emerald-400 dark:hover:bg-rose-950/20 dark:hover:text-rose-400 transition-colors"
                disabled={setPublishedMutation.isPending}
                onClick={() => {
                  if (window.confirm("Unpublish this exam? Students will no longer see it.")) {
                    setPublishedMutation.mutate(false);
                  }
                }}
              >
                {setPublishedMutation.isPending
                  ? <Loader2 className="animate-spin size-3.5" />
                  : <Eye size={13} />
                }
                Published ✓
              </Button>
            ) : (
              <Button
                size="sm"
                className="h-8 text-xs gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground"
                disabled={!existingExam || setPublishedMutation.isPending}
                onClick={() => setPublishedMutation.mutate(true)}
              >
                {setPublishedMutation.isPending
                  ? <Loader2 className="animate-spin size-3.5" />
                  : <Send size={13} />
                }
                Publish
              </Button>
            )}
          </div>
        </div>

        {/* Main 3-Panel Workspace Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 flex-1 min-h-0 items-stretch mt-2">

          {/* PANEL 1: Left Structure Navigator (xl:col-span-3) */}
          <div className="xl:col-span-3 flex flex-col bg-card border border-border/80 rounded-xl shadow-sm min-h-[500px] xl:min-h-0 xl:h-full overflow-hidden">
            <div className="flex items-center justify-between border-b border-border/40 px-4 pt-4 pb-3 shrink-0">
              <div className="space-y-0.5">
                <h3 className="font-bold text-sm text-foreground flex items-center gap-1.5">
                  <BookOpen className="size-4 text-primary" /> Exam Blueprint Flow
                </h3>
                <p className="text-[10px] text-muted-foreground">
                  Total Questions: <span className="font-semibold text-foreground">
                    {unifiedFlow.reduce((acc, item) => {
                      if (item.type === "scenario") {
                        const subQsCount = existingEditUi.filter(q => q.scenarioId === item.id).length +
                          draftQuestions.filter(q => q.scenarioId === item.id).length;
                        return acc + subQsCount;
                      }
                      return acc + 1;
                    }, 0)}
                  </span>
                  {" • "}
                  Marks: <span className="font-semibold text-foreground">{totalMarksSum}</span>
                </p>
              </div>
              <span className="text-xs text-muted-foreground font-mono bg-muted/60 px-2 py-0.5 rounded border border-border/40">
                Nodes: {unifiedFlow.length}
              </span>
            </div>

            {unifiedFlow.length === 0 ? (
              <div className="flex-1 overflow-y-auto p-4 scrollbar-thin">
                <div className="flex flex-col items-center justify-center py-20 text-center h-full border border-dashed border-border/60 rounded-lg bg-secondary/5">
                  <HelpCircle className="size-8 text-muted-foreground/60 mb-2 animate-pulse" />
                  <p className="text-xs text-muted-foreground font-medium">No items added yet</p>
                  <p className="text-[10px] text-muted-foreground/80 mt-1 max-w-[180px]">
                    Use the "Add Question" dropdown above to add content.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-4 scrollbar-thin">
                <div className="space-y-2.5 pr-1">
                  <AnimatePresence initial={false}>
                    {unifiedFlow.map((item, index) => {
                      const isActive = activeItemId === item.id;
                      const itemLabel = `${index + 1}`;

                      // ── Scenario Node ──────────────────────────────────
                      if (item.type === "scenario") {
                        const s = item.scenario;
                        const subQuestions = [
                          ...existingEditUi.filter(q => q.scenarioId === s.id),
                          ...draftQuestions.filter(q => q.scenarioId === s.id),
                        ].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
                        const isCollapsed = collapsedScenarios[s.id] || false;

                        return (
                          <motion.div
                            key={s.id}
                            layout
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.18 }}
                            className={`rounded-xl border transition-all ${isActive
                                ? "border-primary/50 bg-primary/[0.03] ring-1 ring-primary/20"
                                : "border-border/50 hover:border-border bg-secondary/[0.03]"
                              }`}
                          >
                            {/* Scenario Header */}
                            <div
                              className="px-3 py-2.5 flex items-center gap-2 cursor-pointer"
                              onClick={() => setActiveItemId(s.id)}
                            >
                              <button
                                type="button"
                                className="p-0.5 rounded text-muted-foreground hover:text-foreground shrink-0 transition-colors"
                                onClick={e => {
                                  e.stopPropagation();
                                  setCollapsedScenarios(prev => ({ ...prev, [s.id]: !isCollapsed }));
                                }}
                              >
                                <ChevronRight
                                  size={13}
                                  className={`transition-transform duration-200 ${!isCollapsed ? "rotate-90" : ""}`}
                                />
                              </button>
                              <span className="text-[9px] font-bold text-primary bg-primary/10 border border-primary/20 px-1.5 py-0.5 rounded font-mono shrink-0">
                                BLOCK {itemLabel}
                              </span>
                              <span className="text-xs font-semibold text-foreground truncate flex-1">
                                {s.audioUrl ? "🎧" : s.imageUrl ? "🖼️" : "📝"}{" "}
                                {s.instructionsText
                                  ? s.instructionsText.slice(0, 22) + (s.instructionsText.length > 22 ? "…" : "")
                                  : "Scenario Passage"
                                }
                              </span>
                              <div className="flex items-center gap-0.5 shrink-0" onClick={e => e.stopPropagation()}>
                                <button
                                  className="size-5 flex items-center justify-center rounded text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                                  disabled={index === 0}
                                  onClick={() => handleMoveTopLevelItem(index, "up")}
                                >
                                  <ArrowUp size={10} />
                                </button>
                                <button
                                  className="size-5 flex items-center justify-center rounded text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                                  disabled={index === unifiedFlow.length - 1}
                                  onClick={() => handleMoveTopLevelItem(index, "down")}
                                >
                                  <ArrowDown size={10} />
                                </button>
                                <button
                                  className="size-5 flex items-center justify-center rounded text-destructive hover:bg-destructive/10 transition-colors"
                                  onClick={() => setDeleteConfirmId(s.id)}
                                >
                                  <Trash2 size={10} />
                                </button>
                              </div>
                            </div>

                            {/* Nested Questions */}
                            {!isCollapsed && (
                              <div className="px-3 pb-3 border-t border-border/30">
                                <div className="pl-3 border-l-2 border-primary/15 pt-2 space-y-1.5">
                                  {subQuestions.map((q, qIdx) => {
                                    const isSubActive = activeItemId === q.id;
                                    const isDraft = draftQuestions.some(d => d.id === q.id);

                                    return (
                                      <div
                                        key={q.id}
                                        className={`px-2 py-1.5 rounded-xl border text-xs flex items-center justify-between gap-1.5 cursor-pointer transition-all ${isSubActive
                                            ? "border-primary/40 bg-primary/[0.08] text-primary"
                                            : "border-border/30 hover:border-border/70 text-muted-foreground hover:text-foreground bg-background/40"
                                          }`}
                                        onClick={() => setActiveItemId(q.id)}
                                      >
                                        <span className="truncate flex items-center gap-1.5 min-w-0">
                                          {isDraft && (
                                            <span className="text-[8px] font-bold text-amber-500 bg-amber-500/10 px-1 py-0.5 rounded border border-amber-500/20 shrink-0">
                                              DRAFT
                                            </span>
                                          )}
                                          <span className={`text-[8px] font-bold px-1 py-0.5 rounded border shrink-0 bg-blue-500/10 text-blue-600 border-blue-500/20`}>
                                            QST
                                          </span>
                                          <span className="font-mono text-[9px] opacity-60 shrink-0">
                                            Q{itemLabel}.{qIdx + 1}
                                          </span>
                                          <span className="truncate font-japanese">
                                            {q.questionText || "(empty)"}
                                          </span>
                                        </span>
                                        <div className="flex items-center gap-0.5 shrink-0" onClick={e => e.stopPropagation()}>
                                          <button
                                            className="size-4 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                                            disabled={qIdx === 0}
                                            onClick={() => {
                                              if (isDraft) handleMoveDraft(q.id, "up", s.id);
                                              else handleMoveExistingQuestion(q.id, "up", s.id);
                                            }}
                                          >
                                            <ArrowUp size={8} />
                                          </button>
                                          <button
                                            className="size-4 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                                            disabled={qIdx === subQuestions.length - 1}
                                            onClick={() => {
                                              if (isDraft) handleMoveDraft(q.id, "down", s.id);
                                              else handleMoveExistingQuestion(q.id, "down", s.id);
                                            }}
                                          >
                                            <ArrowDown size={8} />
                                          </button>
                                          <button
                                            className="size-4 flex items-center justify-center text-muted-foreground hover:text-primary transition-colors"
                                            onClick={() => handleDuplicateQuestion(q)}
                                          >
                                            <Copy size={8} />
                                          </button>
                                          <button
                                            className="size-4 flex items-center justify-center text-destructive transition-colors"
                                            onClick={() => handleDeleteQuestion(q.id, isDraft)}
                                          >
                                            <Trash2 size={8} />
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}

                                  {/* Add nested question */}
                                  <button
                                    type="button"
                                    className="w-full text-left text-[10px] text-primary font-medium px-2 py-1.5 rounded-md border border-dashed border-primary/25 hover:bg-primary/5 transition-colors flex items-center gap-1"
                                    onClick={() => {
                                      const d = emptyUiQuestion(s.id);
                                      const maxSort = Math.max(
                                        ...draftQuestions.filter(q => q.scenarioId === s.id).map(q => q.sortOrder),
                                        ...existingEditUi.filter(q => q.scenarioId === s.id).map(q => q.sortOrder),
                                        -1
                                      );
                                      d.sortOrder = maxSort + 1;
                                      setDraftQuestions(prev => [...prev, d]);
                                      setActiveItemId(d.id);
                                    }}
                                  >
                                    <Plus size={9} />
                                    Add nested question
                                  </button>
                                </div>
                              </div>
                            )}
                          </motion.div>
                        );
                      }

                      // ── Standalone Question Node ───────────────────────
                      const q = item.question;
                      const isDraft = item.type === "draft-standalone";


                      return (
                        <motion.div
                          key={q.id}
                          layout
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.18 }}
                          className={`px-3 py-2.5 rounded-xl border flex items-center gap-2 cursor-pointer transition-all ${isActive
                              ? "border-primary/50 bg-primary/[0.03] ring-1 ring-primary/20"
                              : "border-border/50 hover:border-border bg-card hover:bg-secondary/10"
                            }`}
                          onClick={() => setActiveItemId(q.id)}
                        >
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border shrink-0 bg-blue-500/10 text-blue-600 border-blue-500/20`}>
                            QST
                          </span>
                          <span className="text-[10px] font-mono text-muted-foreground/60 shrink-0">Q{itemLabel}</span>
                          <span className="text-xs font-medium truncate flex-1 flex items-center gap-1.5">
                            {isDraft && (
                              <span className="text-[8px] font-bold text-amber-500 bg-amber-500/10 px-1 py-0.5 rounded border border-amber-500/20 shrink-0">
                                DRAFT
                              </span>
                            )}
                            <span className="truncate font-japanese">
                              {q.questionText || "Standalone Question"}
                            </span>
                          </span>
                          <div className="flex items-center gap-0.5 shrink-0" onClick={e => e.stopPropagation()}>
                            <button
                              className="size-5 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                              disabled={index === 0}
                              onClick={() => handleMoveTopLevelItem(index, "up")}
                            >
                              <ArrowUp size={10} />
                            </button>
                            <button
                              className="size-5 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                              disabled={index === unifiedFlow.length - 1}
                              onClick={() => handleMoveTopLevelItem(index, "down")}
                            >
                              <ArrowDown size={10} />
                            </button>
                            <button
                              className="size-5 flex items-center justify-center text-muted-foreground hover:text-primary transition-colors"
                              onClick={() => handleDuplicateQuestion(q)}
                            >
                              <Copy size={10} />
                            </button>
                            <button
                              className="size-5 flex items-center justify-center text-destructive transition-colors"
                              onClick={() => handleDeleteQuestion(q.id, isDraft)}
                            >
                              <Trash2 size={10} />
                            </button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              </div>
            )}
          </div>

          {/* PANEL 2: Center Editor Panel (xl:col-span-5) */}
          <div className="xl:col-span-5 flex flex-col bg-card border border-border/80 rounded-xl shadow-sm min-h-[600px] xl:min-h-0 xl:h-full overflow-hidden">
            {activeQuestion ? (
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between border-b border-border/40 px-5 pt-5 pb-3 shrink-0">
                  <h3 className="font-bold text-sm text-foreground flex items-center gap-1.5">
                    <Edit3 className="size-4 text-primary" /> Question Content Editor
                  </h3>
                  <Button
                    size="sm"
                    className="h-8"
                    disabled={savingQuestionId === activeQuestion.id}
                    onClick={() => {
                      const isExisting = existingEditUi.some(q => q.id === activeQuestion.id);
                      if (isExisting) {
                        saveQuestionMutation.mutate(activeQuestion);
                      } else {
                        toast.success("Draft updated! Click Publish above to commit to the exam.");
                      }
                    }}
                  >
                    {savingQuestionId === activeQuestion.id ? <Loader2 className="animate-spin size-3.5 mr-1" /> : <Save className="size-3.5 mr-1" />}
                    Save Change
                  </Button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-5 scrollbar-thin">
                  {/* Section & Marks Input Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold">Section Category</Label>
                      <select
                        className="w-full h-9 px-2.5 rounded-lg border border-border bg-background text-xs font-medium focus:ring-1 focus:ring-primary outline-none"
                        value="QUESTION"
                        onChange={(e) => {
                          const val = e.target.value as any;
                          updateActiveQuestion(q => ({ ...q, examCategory: val }));
                        }}
                      >
                        <option value="VOCABULARY">言語知識（文字・語彙）- Vocabulary</option>
                        <option value="GRAMMAR">言語知識（文法）- Grammar</option>
                        <option value="READING">読解 (Reading)</option>
                        <option value="LISTENING">聴解 (Listening)</option>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold">Question Marks</Label>
                      <Input
                        className="h-9 text-xs"
                        type="number"
                        min={0}
                        value={activeQuestion.marks ?? 1}
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          updateActiveQuestion(q => ({ ...q, marks: Number.isFinite(val) ? val : 1 }));
                        }}
                      />
                    </div>
                  </div>

                  {/* Question Stem Text Area */}
                  <div className="space-y-2 flex-1 flex flex-col">
                    <div className="flex justify-between items-center shrink-0">
                      <Label className="text-xs font-semibold">Question Stem / Prompt</Label>
                      <TextFormattingToolbar
                        textareaRef={questionStemRef}
                        value={activeQuestion.questionText}
                        onChange={(val) => updateActiveQuestion(q => ({ ...q, questionText: val }))}
                      />
                    </div>
                    <textarea
                      ref={questionStemRef}
                      className="w-full flex-1 min-h-[100px] max-h-[220px] p-3 rounded-lg border border-border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none font-japanese leading-relaxed"
                      value={activeQuestion.questionText}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateActiveQuestion(q => ({ ...q, questionText: val }));
                      }}
                      placeholder="e.g. 下の文の{ }に入る最も良いものを選びなさい。"
                    />
                    <p className="text-[10px] text-muted-foreground">Tip: Use Kanji{`{Furigana}`} pattern to render inline ruby text.</p>
                  </div>

                  {/* Media attachments */}
                  <div className="space-y-2.5">
                    <Label className="text-xs font-semibold">Attach Media (Question Level)</Label>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <label className="text-xs text-primary border border-primary/20 bg-primary/[0.01] hover:bg-primary/5 rounded-lg p-2.5 flex items-center justify-center gap-1.5 cursor-pointer font-semibold transition-all">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const r = await uploadToS3(file, "exam-question");
                              updateActiveQuestion(q => ({ ...q, imageUrl: r.url }));
                              toast.success("Image attached");
                            } catch {
                              toast.error("Upload failed");
                            }
                          }}
                        />
                        <ImageIcon className="size-4 shrink-0" />
                        <span>Upload Image</span>
                      </label>
                      <label className="text-xs text-primary border border-primary/20 bg-primary/[0.01] hover:bg-primary/5 rounded-lg p-2.5 flex items-center justify-center gap-1.5 cursor-pointer font-semibold transition-all">
                        <input
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const r = await uploadToS3(file, "exam-question");
                              updateActiveQuestion(q => ({ ...q, audioUrl: r.url }));
                              toast.success("Audio attached");
                            } catch {
                              toast.error("Upload failed");
                            }
                          }}
                        />
                        <Volume2 className="size-4 shrink-0" />
                        <span>Upload Audio</span>
                      </label>
                    </div>

                    {(activeQuestion.imageUrl || activeQuestion.audioUrl) && (
                      <div className="rounded-lg border border-border/80 p-2.5 bg-secondary/15 space-y-2">
                        {activeQuestion.imageUrl && (
                          <div className="relative inline-block">
                            <img src={activeQuestion.imageUrl} alt="" className="max-h-24 object-contain rounded border border-border" />
                            <Button
                              variant="destructive"
                              size="icon"
                              className="size-5 absolute -top-1.5 -right-1.5 rounded-full"
                              onClick={() => updateActiveQuestion(q => ({ ...q, imageUrl: "" }))}
                            >
                              ×
                            </Button>
                          </div>
                        )}
                        {activeQuestion.audioUrl && (
                          <div className="flex items-center gap-2 bg-background p-1.5 rounded border border-border">
                            <audio controls className="w-full h-8" src={activeQuestion.audioUrl} />
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-7 text-destructive rounded-md"
                              onClick={() => updateActiveQuestion(q => ({ ...q, audioUrl: "" }))}
                            >
                              <Trash2 size={13} />
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* MCQ Options List */}
                  <div className="space-y-3.5 overflow-y-auto max-h-[420px] pr-1">
                    <div>
                      <Label className="text-xs font-semibold">Option Layout / Type</Label>
                      <div className="flex rounded-lg border border-border p-0.5 bg-secondary/20 gap-0.5 mt-1 select-none">
                        {(["TEXT", "IMAGE", "MIXED"] as const).map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => {
                              updateActiveQuestion(q => ({
                                ...q,
                                optionType: t,
                              }));
                            }}
                            className={cn(
                              "flex-1 py-1 text-[11px] font-semibold rounded-md transition-all",
                              (activeQuestion.optionType || "TEXT") === t
                                ? "bg-background text-foreground shadow-xs"
                                : "text-muted-foreground hover:text-foreground"
                            )}
                          >
                            {t === "TEXT" ? "Text Only" : t === "IMAGE" ? "Image Only" : "Text + Image"}
                          </button>
                        ))}
                      </div>
                    </div>

                    <Label className="text-xs font-semibold block pt-1">Answer Options (MCQ)</Label>
                    <div className="space-y-2.5">
                      {activeQuestion.options.map((opt, oIdx) => {
                        const isCorrect = activeQuestion.correctId === opt.id;
                        const currentLayout = activeQuestion.optionType || "TEXT";
                        return (
                          <div key={opt.id} className={`p-2.5 rounded-xl border space-y-2.5 transition-all ${isCorrect ? "border-emerald-500/40 bg-emerald-500/[0.03]" : "border-border/60 bg-secondary/5"}`}>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => updateActiveQuestion(q => ({ ...q, correctId: opt.id }))}
                                className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 border text-xs font-bold transition-all ${isCorrect
                                  ? "bg-emerald-500/10 border-emerald-500 text-emerald-600 dark:text-emerald-400"
                                  : "border-border hover:border-emerald-500/50 bg-background text-muted-foreground"
                                  }`}
                              >
                                {mcqDisplayBadge(oIdx)}
                              </button>

                              {currentLayout !== "IMAGE" ? (
                                <div className="relative flex items-center flex-1 min-w-0">
                                  <Input
                                    id={`opt-input-${activeQuestion.id}-${opt.id}`}
                                    className="h-9 text-xs font-japanese flex-1 bg-background pr-8"
                                    value={opt.text}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      updateActiveQuestion(q => ({
                                        ...q,
                                        options: q.options.map(o => o.id === opt.id ? { ...o, text: val } : o)
                                      }));
                                    }}
                                    placeholder={`Option ${mcqDisplayBadge(oIdx)} text`}
                                  />
                                  <button
                                    type="button"
                                    className="absolute right-2 text-primary hover:text-primary/80 transition-all p-1"
                                    title="Insert Kanji{Furigana}"
                                    onClick={() => {
                                      const el = document.getElementById(`opt-input-${activeQuestion.id}-${opt.id}`) as HTMLInputElement;
                                      if (el) {
                                        insertFormatting(el, "furigana", opt.text, (newVal) => {
                                          updateActiveQuestion(q => ({
                                            ...q,
                                            options: q.options.map(o => o.id === opt.id ? { ...o, text: newVal } : o)
                                          }));
                                        });
                                      }
                                    }}
                                  >
                                    <Languages size={13} />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex-1 text-xs text-muted-foreground italic pl-2 py-1 bg-secondary/5 rounded border border-border/30">
                                  Option image will represent Choice {mcqDisplayBadge(oIdx)}
                                </div>
                              )}

                              <div className="flex items-center gap-0.5 shrink-0">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="size-8 text-muted-foreground hover:text-foreground"
                                  disabled={oIdx === 0}
                                  onClick={() => moveOption(oIdx, "up")}
                                  title="Move Up"
                                >
                                  <ArrowUp size={13} />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="size-8 text-muted-foreground hover:text-foreground"
                                  disabled={oIdx === activeQuestion.options.length - 1}
                                  onClick={() => moveOption(oIdx, "down")}
                                  title="Move Down"
                                >
                                  <ArrowDown size={13} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="size-8 text-destructive rounded-lg hover:bg-destructive/10 shrink-0"
                                  disabled={activeQuestion.options.length <= 2}
                                  onClick={() => {
                                    updateActiveQuestion(q => {
                                      const next = q.options.filter(o => o.id !== opt.id);
                                      const correctId = next.some(o => o.id === q.correctId) ? q.correctId : (next[0]?.id ?? "");
                                      return { ...q, options: next, correctId };
                                    });
                                  }}
                                >
                                  <Trash2 size={13} />
                                </Button>
                              </div>
                            </div>

                            {/* Option image attach/preview */}
                            {currentLayout !== "TEXT" && (
                              <div className="flex items-center gap-3 pl-9.5">
                                {opt.imageUrl ? (
                                  <div className="flex items-center gap-2 bg-secondary/20 border border-border p-1.5 rounded-lg">
                                    <img src={opt.imageUrl} alt="" className="size-10 object-contain bg-white rounded border border-border" />
                                    <span className="text-[10px] text-muted-foreground font-sans truncate max-w-[120px]">Image attached</span>
                                    <button
                                      type="button"
                                      onClick={() => updateActiveQuestion(q => ({
                                        ...q,
                                        options: q.options.map(o => o.id === opt.id ? { ...o, imageUrl: "" } : o)
                                      }))}
                                      className="text-destructive font-bold text-xs hover:text-destructive/80 px-1"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ) : (
                                  <label className="cursor-pointer hover:text-primary transition-colors flex items-center gap-1.5 border border-dashed border-border/80 px-2.5 py-1 rounded-lg text-[10px] bg-background hover:bg-secondary/5 select-none">
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        try {
                                          const r = await uploadToS3(file, "exam-option");
                                          updateActiveQuestion(q => ({
                                            ...q,
                                            options: q.options.map(o => o.id === opt.id ? { ...o, imageUrl: r.url } : o)
                                          }));
                                          toast.success("Option image attached");
                                        } catch {
                                          toast.error("Upload failed");
                                        }
                                      }}
                                    />
                                    <ImageIcon className="size-3 shrink-0 text-muted-foreground" />
                                    <span>Upload Choice Image</span>
                                  </label>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full text-xs h-9 border-dashed border-border/80 hover:border-primary/40 rounded-xl bg-background"
                      onClick={() => updateActiveQuestion(q => ({
                        ...q,
                        options: [...q.options, { id: newMcqOptionId(), text: "", imageUrl: "", audioUrl: "" }]
                      }))}
                    >
                      + Add Answer Option Choice
                    </Button>
                  </div>

                  {/* ── Answer Explanation ─────────────────────────────────── */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold flex items-center gap-1.5">
                      <span>解説 / Answer Explanation</span>
                      <span className="text-[10px] font-normal text-muted-foreground">(Optional — shown to students in review mode)</span>
                    </Label>
                    <textarea
                      className="w-full min-h-[72px] p-3 rounded-xl border border-border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none font-japanese leading-relaxed"
                      value={activeQuestion.answerExplanation ?? ""}
                      onChange={e => updateActiveQuestion(q => ({ ...q, answerExplanation: e.target.value }))}
                      placeholder="e.g. この答えが正しい理由は… / Explain why this is the correct answer..."
                    />
                  </div>

                </div>
              </div>
              /* ── Scenario Passage Editor ─────────────────────────────── */
            ) : activeScenario ? (
              <div className="flex flex-col h-full">
                {/* Scenario Editor Header */}
                <div className="px-5 pt-5 pb-3.5 border-b border-border/50 flex items-center justify-between bg-card shrink-0">
                  <h3 className="text-xs font-bold text-foreground flex items-center gap-1.5">
                    <Layers className="size-3.5 text-primary" />
                    Scenario Block Editor
                  </h3>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      className="h-7 text-xs gap-1.5 rounded-xl"
                      disabled={scenarioMediaSavingId === activeScenario.id || isGlobalSaving}
                      onClick={() => updateScenarioMediaMutation.mutate({
                        scenarioId: activeScenario.id,
                        data: { instructionsText: editingScenarioText.trim() || null },
                      })}
                    >
                      {scenarioMediaSavingId === activeScenario.id
                        ? <Loader2 className="animate-spin size-3" />
                        : <Save size={12} />
                      }
                      Save Change
                    </Button>
                    <button
                      className="size-7 flex items-center justify-center text-destructive hover:bg-destructive/10 rounded-xl transition-colors"
                      onClick={() => setDeleteConfirmId(activeScenario.id)}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-5 scrollbar-thin">
                  {/* Passage Text */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold">Passage / Instructions</Label>
                      <TextFormattingToolbar
                        textareaRef={scenarioPassageRef}
                        value={editingScenarioId === activeScenario.id ? editingScenarioText : (activeScenario.instructionsText ?? "")}
                        onChange={val => {
                          setEditingScenarioId(activeScenario.id);
                          setEditingScenarioText(val);
                        }}
                      />
                    </div>
                    <textarea
                      ref={scenarioPassageRef}
                      className="w-full min-h-[140px] p-3 rounded-xl border border-border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none font-japanese leading-relaxed"
                      value={editingScenarioId === activeScenario.id ? editingScenarioText : (activeScenario.instructionsText ?? "")}
                      onChange={e => {
                        setEditingScenarioId(activeScenario.id);
                        setEditingScenarioText(e.target.value);
                      }}
                      onBlur={() => {
                        if (editingScenarioId === activeScenario.id) {
                          updateScenarioMediaMutation.mutate({
                            scenarioId: activeScenario.id,
                            data: { instructionsText: editingScenarioText.trim() || null },
                          });
                        }
                      }}
                      placeholder="Type the reading passage or listening instructions here..."
                    />
                  </div>

                  {/* Passage Media */}
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">Passage Media</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex items-center justify-center gap-1.5 h-9 rounded-xl border border-border/60 hover:border-primary/30 text-muted-foreground hover:text-primary text-xs font-medium cursor-pointer transition-all">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async e => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const r = await uploadToS3(file, "mock-test-scenario");
                              updateScenarioMediaMutation.mutate({ scenarioId: activeScenario.id, data: { imageUrl: r.url } });
                            } catch { toast.error("Upload failed"); }
                          }}
                        />
                        <ImageIcon size={13} />
                        Attach Image
                      </label>
                      <label className="flex items-center justify-center gap-1.5 h-9 rounded-xl border border-border/60 hover:border-purple-500/30 text-muted-foreground hover:text-purple-500 text-xs font-medium cursor-pointer transition-all">
                        <input
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          onChange={async e => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const r = await uploadToS3(file, "mock-test-scenario");
                              updateScenarioMediaMutation.mutate({ scenarioId: activeScenario.id, data: { audioUrl: r.url } });
                            } catch { toast.error("Upload failed"); }
                          }}
                        />
                        <AudioLines size={13} />
                        Attach Audio
                      </label>
                    </div>

                    {(activeScenario.imageUrl || activeScenario.audioUrl) && (
                      <div className="rounded-xl border border-border/70 p-3 bg-secondary/10 space-y-2">
                        {activeScenario.imageUrl && (
                          <div className="relative inline-block">
                            <img src={activeScenario.imageUrl} alt="" className="max-h-28 rounded-lg border border-border object-contain" />
                            <button
                              className="absolute -top-1.5 -right-1.5 size-5 bg-destructive text-white rounded-full text-xs flex items-center justify-center hover:bg-destructive/80"
                              onClick={() => updateScenarioMediaMutation.mutate({ scenarioId: activeScenario.id, data: { imageUrl: null } })}
                            >×</button>
                          </div>
                        )}
                        {activeScenario.audioUrl && (
                          <div className="flex items-center gap-2 bg-background p-2 rounded-lg border border-border">
                            <audio controls className="flex-1 h-8" src={activeScenario.audioUrl} />
                            <button
                              className="text-destructive hover:text-destructive/80 transition-colors"
                              onClick={() => updateScenarioMediaMutation.mutate({ scenarioId: activeScenario.id, data: { audioUrl: null } })}
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Listening CBT Controls */}
                  {activeScenario.audioUrl && (
                    <div className="p-3 bg-purple-500/5 rounded-xl border border-purple-500/20 text-xs space-y-3">
                      <span className="font-bold text-foreground flex items-center gap-1.5">
                        <AudioLines size={13} className="text-purple-500" />
                        Listening CBT Controls
                      </span>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Play Limit</Label>
                        <select className="w-full h-8 rounded-xl border border-border bg-background px-2 text-[11px] outline-none">
                          <option value="1">1× (Official JLPT)</option>
                          <option value="2">2× (Official JFT)</option>
                          <option value="unlimited">Unlimited</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Questions under this scenario */}
                  {(() => {
                    const subQs = [
                      ...existingEditUi.filter(q => q.scenarioId === activeScenario.id),
                      ...draftQuestions.filter(q => q.scenarioId === activeScenario.id),
                    ].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

                    return (
                      <div className="space-y-2 pt-1">
                        <div className="flex items-center gap-2 border-t border-border/40 pt-4">
                          <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                            Questions in this Scenario ({subQs.length})
                          </Label>
                        </div>
                        {subQs.length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-6 border border-dashed border-border/60 rounded-xl">
                            No questions yet. Click the left panel to add one.
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {subQs.map((q, qIdx) => {
                              const isDraft = draftQuestions.some(d => d.id === q.id);
                              return (
                                <div
                                  key={q.id}
                                  className="p-3 rounded-xl border border-border/50 bg-background/60 space-y-2 cursor-pointer hover:border-primary/30 transition-all"
                                  onClick={() => setActiveItemId(q.id)}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                      {isDraft && (
                                        <span className="text-[8px] font-bold text-amber-500 bg-amber-500/10 px-1 py-0.5 rounded border border-amber-500/20">
                                          DRAFT
                                        </span>
                                      )}
                                      <span className="text-[10px] font-mono text-muted-foreground">Q{qIdx + 1}</span>
                                    </span>
                                    <span className="text-[10px] text-muted-foreground">{q.marks ?? 1} 点</span>
                                  </div>
                                  <p className="text-xs font-japanese text-foreground leading-snug line-clamp-2">
                                    {q.questionText || "(no question text)"}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <button
                          type="button"
                          className="w-full h-9 rounded-xl border border-dashed border-border/60 hover:border-primary/35 text-xs font-medium text-muted-foreground hover:text-primary transition-all flex items-center justify-center gap-1.5"
                          onClick={() => {
                            const d = emptyUiQuestion();
                            d.scenarioId = activeScenario.id;
                            const maxSort = Math.max(
                              ...draftQuestions.filter(q => q.scenarioId === activeScenario.id).map(q => q.sortOrder),
                              ...existingEditUi.filter(q => q.scenarioId === activeScenario.id).map(q => q.sortOrder),
                              -1
                            );
                            d.sortOrder = maxSort + 1;
                            setDraftQuestions(prev => [...prev, d]);
                            setActiveItemId(d.id);
                          }}
                        >
                          <Plus size={13} />
                          Add Question to this Scenario
                        </button>
                      </div>
                    );
                  })()}
                </div>
              </div>

            ) : (
              /* ── Empty State ─────────────────────────────────────────── */
              <div className="flex-1 flex flex-col items-center justify-center text-center gap-4 p-10 text-muted-foreground h-full">
                <div className="w-16 h-16 rounded-2xl bg-primary/5 border border-primary/10 flex items-center justify-center text-primary">
                  <Edit3 className="size-7" />
                </div>
                <div>
                  <h4 className="font-bold text-foreground text-sm">Authoring Workspace</h4>
                  <p className="text-xs max-w-xs mx-auto mt-1.5 leading-relaxed">
                    Select any question or scenario block from the left panel to begin editing.
                    Use the "Add Question" button to create new content.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ── RIGHT: CBT Live Preview ───────────────────────────────────── */}
          <div className="xl:col-span-4 flex flex-col bg-card border border-border/70 rounded-2xl overflow-hidden shadow-sm min-h-[600px] xl:min-h-0 xl:h-full">
            {/* Preview Header */}
            <div className="px-5 py-3.5 border-b border-border/50 flex items-center justify-between bg-card shrink-0">
              <h3 className="text-xs font-bold text-foreground flex items-center gap-1.5">
                <Eye className="size-3.5 text-emerald-500" />
                CBT Live Preview
              </h3>
              <span className="text-[9px] uppercase font-bold tracking-wider text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                Live Simulator
              </span>
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-thin select-none flex flex-col bg-[#edece9]">
              {/* Mini Slate CBT Header */}
              <div className="bg-slate-900 border-b border-slate-800 text-slate-100 px-4 py-2.5 flex items-center justify-between text-[10px] font-sans font-bold shrink-0">
                <span>日本語能力試験 (JLPT) SIMULATION</span>
                <span className="text-slate-400 font-mono">CBT SCREEN</span>
              </div>

              {activeScenario || (activeQuestion && activeQuestion.scenarioId) ? (
                /* ── Full Scenario Preview ─────────────────────────────── */
                <div className="p-4 space-y-4 flex-1">
                  {(() => {
                    const targetScenario = activeScenario || examScenarios.find(s => s.id === activeQuestion?.scenarioId);
                    if (!targetScenario) return null;
                    const isEditing = activeScenario?.id === targetScenario.id;
                    const textToShow = isEditing && editingScenarioId === targetScenario.id
                      ? editingScenarioText
                      : (targetScenario.instructionsText ?? "");

                    return (
                      <>
                        {/* Scenario Passage Card */}
                        <div
                          className="bg-[#fcfaf6] border border-stone-200 shadow-sm rounded-xl overflow-hidden"

                        >
                          {/* Card Header */}
                          <div className="bg-stone-50 border-b border-stone-200 px-5 py-2 flex items-center justify-between">
                            <span className="text-[9px] uppercase font-bold tracking-widest text-stone-500 font-sans">
                              問題文 · Shared Context Passage
                            </span>
                            {isEditing && (
                              <span className="text-[8px] font-bold text-stone-600 bg-stone-200 px-1.5 py-0.5 rounded font-sans animate-pulse">
                                Editing
                              </span>
                            )}
                          </div>

                          <div className="p-5 space-y-4">
                            {textToShow ? (
                              <div className="text-[14px] leading-loose text-stone-900 whitespace-pre-wrap">
                                {renderFurigana(textToShow)}
                              </div>
                            ) : (
                              <p className="text-xs text-stone-400 italic font-sans py-4 text-center">
                                (No passage text entered yet)
                              </p>
                            )}
                            {targetScenario.imageUrl && (
                              <img
                                src={targetScenario.imageUrl}
                                alt=""
                                className="max-h-48 rounded-sm border border-stone-200 bg-white/40 object-contain w-full"
                              />
                            )}
                            {targetScenario.audioUrl && (
                              <div className="bg-stone-50 p-3 border border-stone-200 rounded-sm font-sans">
                                <p className="text-[9px] text-stone-500 font-semibold uppercase tracking-wider mb-2 flex items-center gap-1">
                                  <AudioLines size={10} /> CBT Audio Directive
                                </p>
                                <audio controls className="w-full h-8" src={targetScenario.audioUrl} />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Connected Questions */}
                        {(() => {
                          const targetId = activeScenario?.id || activeQuestion?.scenarioId;
                          const linked = [
                            ...existingEditUi.filter(q => q.scenarioId === targetId),
                            ...draftQuestions.filter(q => q.scenarioId === targetId),
                          ].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

                          return linked.map((q, idx) => {
                            const isEditingThis = activeQuestion?.id === q.id;
                            return (
                              <div
                                key={q.id}
                                className={`bg-white border shadow-sm rounded-xl overflow-hidden transition-all ${isEditingThis ? "border-slate-800 ring-1 ring-slate-800" : "border-stone-200"
                                  }`}

                              >
                                {/* Q Header */}
                                <div className="flex items-center justify-between px-5 py-2 border-b border-stone-200 bg-stone-50/60">
                                  <span className="text-[9px] uppercase font-bold tracking-widest text-stone-600 font-sans">
                                    問題 {idx + 1} · {q.examCategory}
                                  </span>
                                  <span className="text-[9px] font-sans text-stone-500">
                                    {q.marks ?? 1} 点
                                  </span>
                                </div>

                                <div className="p-5 space-y-4">
                                  <div className="text-[14px] leading-loose text-stone-900 font-medium whitespace-pre-wrap">
                                    {renderFurigana(q.questionText || "(Question text not entered...)")}
                                  </div>
                                  {q.imageUrl && (
                                    <img src={q.imageUrl} alt="" className="max-h-36 rounded-sm border border-stone-200 w-full object-contain" />
                                  )}
                                  {q.audioUrl && (
                                    <div className="bg-stone-50 p-2 border border-stone-200 rounded-sm font-sans">
                                      <audio controls className="w-full h-8" src={q.audioUrl} />
                                    </div>
                                  )}
                                  {/* MCQ Options */}
                                  {(() => {
                                    const qMeta = (q.options as { metadata?: any })?.metadata || {};
                                    const optionType = qMeta.optionType || "TEXT";

                                    if (optionType === "IMAGE") {
                                      return (
                                        <div className="grid grid-cols-2 gap-2 pt-1">
                                          {q.options.map((opt, oIdx) => {
                                            const isCorrect = q.correctId === opt.id;
                                            return (
                                              <div
                                                key={opt.id}
                                                className={cn(
                                                  "relative flex flex-col items-center justify-center rounded-xl border p-2.5 aspect-square bg-white shadow-xs",
                                                  isCorrect
                                                    ? "border-emerald-600 bg-emerald-50/15 text-stone-950 font-semibold ring-1 ring-emerald-500"
                                                    : "border-stone-200 text-stone-800"
                                                )}
                                              >
                                                <span className={cn(
                                                  "absolute top-2 left-2 text-sm font-bold select-none leading-none",
                                                  isCorrect ? "text-emerald-700" : "text-stone-300"
                                                )}>
                                                  {getCircleBadge(oIdx)}
                                                </span>
                                                {opt.imageUrl ? (
                                                  <img src={opt.imageUrl} alt="" className="max-h-[85%] max-w-[85%] object-contain rounded" />
                                                ) : (
                                                  <span className="text-[10px] text-muted-foreground italic">(No Image)</span>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      );
                                    }

                                    if (optionType === "MIXED") {
                                      return (
                                        <div className="space-y-2">
                                          {q.options.map((opt, oIdx) => {
                                            const isCorrect = q.correctId === opt.id;
                                            return (
                                              <div
                                                key={opt.id}
                                                className={cn(
                                                  "flex items-start gap-3 px-4 py-2.5 border rounded-xl shadow-xs text-base bg-white",
                                                  isCorrect
                                                    ? "border-emerald-600 bg-emerald-50/15 text-stone-950 font-semibold ring-1 ring-emerald-500"
                                                    : "border-stone-200 text-stone-800"
                                                )}
                                              >
                                                <span className={cn(
                                                  "text-lg shrink-0 font-bold leading-none pt-0.5",
                                                  isCorrect ? "text-emerald-700" : "text-stone-300"
                                                )}>
                                                  {getCircleBadge(oIdx)}
                                                </span>
                                                <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
                                                  <span className="text-[13px] block leading-snug">
                                                    {renderFurigana(opt.text || `Option ${getCircleBadge(oIdx)}`)}
                                                  </span>
                                                  {opt.imageUrl && (
                                                    <div className="w-16 h-12 shrink-0 border border-stone-200 rounded overflow-hidden bg-white flex items-center justify-center p-0.5">
                                                      <img src={opt.imageUrl} alt="" className="max-h-full max-w-full object-contain rounded" />
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      );
                                    }

                                    // Default TEXT layout
                                    return (
                                      <div className="space-y-2">
                                        {q.options.map((opt, oIdx) => {
                                          const isCorrect = q.correctId === opt.id;
                                          return (
                                            <div
                                              key={opt.id}
                                              className={cn(
                                                "flex items-start gap-3 px-4 py-3 border rounded-xl transition-all duration-200 shadow-xs text-base bg-white",
                                                isCorrect
                                                  ? "border-emerald-650 bg-emerald-50/15 text-stone-950 font-semibold ring-1 ring-emerald-550"
                                                  : "border-stone-200 text-stone-800"
                                              )}
                                            >
                                              <span className={cn(
                                                "text-lg shrink-0 font-bold leading-none pt-0.5 transition-colors",
                                                isCorrect ? "text-slate-900" : "text-stone-300"
                                              )}>
                                                {getCircleBadge(oIdx)}
                                              </span>
                                              <div className="min-w-0 flex-1 pt-0.5">
                                                {(opt.text || !opt.imageUrl) && (
                                                  <span className="text-[13px] block leading-snug">
                                                    {renderFurigana(opt.text || `Option ${getCircleBadge(oIdx)}`)}
                                                  </span>
                                                )}
                                                {opt.imageUrl && (
                                                  <img src={opt.imageUrl} alt="" className="max-h-16 rounded-sm border border-stone-200 object-contain mt-1" />
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </>
                    );
                  })()}
                </div>
              ) : activeQuestion ? (
                /* ── Standalone Question Preview ───────────────────────── */
                <div className="p-4 space-y-4 flex-1">
                  <div
                    className="bg-white border border-stone-205 shadow-sm rounded-xl overflow-hidden"

                  >
                    {/* Q Header */}
                    <div className="flex items-center justify-between px-5 py-2 border-b border-stone-200 bg-stone-50/60">
                      <span className="text-[9px] uppercase font-bold tracking-widest text-stone-600 font-sans">
                        問題 · {activeQuestion.examCategory}
                      </span>
                      <span className="text-[9px] font-sans text-stone-500">
                        {activeQuestion.marks ?? 1} 点
                      </span>
                    </div>

                    <div className="p-5 space-y-4">
                      <div className="text-[14px] leading-loose text-stone-900 font-medium whitespace-pre-wrap">
                        {renderFurigana(activeQuestion.questionText || "(Input question stem text...)")}
                      </div>
                      {activeQuestion.imageUrl && (
                        <img src={activeQuestion.imageUrl} alt="" className="max-h-40 rounded-sm border border-stone-200 w-full object-contain" />
                      )}
                      {activeQuestion.audioUrl && (
                        <div className="bg-stone-50 p-2 border border-stone-200 rounded-sm font-sans">
                          <audio controls className="w-full h-8" src={activeQuestion.audioUrl} />
                        </div>
                      )}
                      {/* Options choices */}
                      {(() => {
                        const optionType = activeQuestion.optionType || "TEXT";

                        if (optionType === "IMAGE") {
                          return (
                            <div className="grid grid-cols-2 gap-2 pt-1 select-none">
                              {activeQuestion.options.map((opt, oIdx) => {
                                const isCorrect = activeQuestion.correctId === opt.id;
                                return (
                                  <div
                                    key={opt.id}
                                    className={cn(
                                      "relative flex flex-col items-center justify-center rounded-xl border p-2.5 aspect-square bg-white shadow-xs",
                                      isCorrect
                                        ? "border-emerald-600 bg-emerald-50/15 text-stone-950 font-semibold ring-1 ring-emerald-500"
                                        : "border-stone-200 text-stone-800"
                                    )}
                                  >
                                    <span className={cn(
                                      "absolute top-2 left-2 text-sm font-bold select-none leading-none",
                                      isCorrect ? "text-emerald-700" : "text-stone-300"
                                    )}>
                                      {getCircleBadge(oIdx)}
                                    </span>
                                    {opt.imageUrl ? (
                                      <img src={opt.imageUrl} alt="" className="max-h-[85%] max-w-[85%] object-contain rounded" />
                                    ) : (
                                      <span className="text-[10px] text-muted-foreground italic">(No Image)</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        }

                        if (optionType === "MIXED") {
                          return (
                            <div className="space-y-2 select-none">
                              {activeQuestion.options.map((opt, oIdx) => {
                                const isCorrect = activeQuestion.correctId === opt.id;
                                return (
                                  <div
                                    key={opt.id}
                                    className={cn(
                                      "flex items-start gap-3 px-4 py-2.5 border rounded-xl shadow-xs text-base bg-white",
                                      isCorrect
                                        ? "border-emerald-600 bg-emerald-50/15 text-stone-950 font-semibold ring-1 ring-emerald-500"
                                        : "border-stone-200 text-stone-800"
                                    )}
                                  >
                                    <span className={cn(
                                      "text-lg shrink-0 font-bold leading-none pt-0.5",
                                      isCorrect ? "text-emerald-700" : "text-stone-300"
                                    )}>
                                      {getCircleBadge(oIdx)}
                                    </span>
                                    <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
                                      <span className="text-[13px] block leading-snug">
                                        {renderFurigana(opt.text || `Option ${getCircleBadge(oIdx)}`)}
                                      </span>
                                      {opt.imageUrl && (
                                        <div className="w-16 h-12 shrink-0 border border-stone-200 rounded overflow-hidden bg-white flex items-center justify-center p-0.5">
                                          <img src={opt.imageUrl} alt="" className="max-h-full max-w-full object-contain rounded" />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        }

                        // Default TEXT layout
                        return (
                          <div className="space-y-2 select-none">
                            {activeQuestion.options.map((opt, oIdx) => {
                              const isCorrect = activeQuestion.correctId === opt.id;
                              return (
                                <div
                                  key={opt.id}
                                  className={cn(
                                    "flex items-start gap-3 px-4 py-3 border rounded-xl transition-all duration-200 shadow-xs text-base bg-white",
                                    isCorrect
                                      ? "border-emerald-650 bg-emerald-50/15 text-stone-950 font-semibold ring-1 ring-emerald-550"
                                      : "border-stone-200 text-stone-800"
                                  )}
                                >
                                  <span className={cn(
                                    "text-lg shrink-0 font-bold leading-none pt-0.5 transition-colors",
                                    isCorrect ? "text-slate-900" : "text-stone-300"
                                  )}>
                                    {getCircleBadge(oIdx)}
                                  </span>
                                  <div className="min-w-0 flex-1 pt-0.5">
                                    {(opt.text || !opt.imageUrl) && (
                                      <span className="text-[13px] block leading-snug">
                                        {renderFurigana(opt.text || `Option ${getCircleBadge(oIdx)}`)}
                                      </span>
                                    )}
                                    {opt.imageUrl && (
                                      <img src={opt.imageUrl} alt="" className="max-h-16 rounded-sm border border-stone-200 object-contain mt-1" />
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Preview Metadata Card */}
                  <div className="mt-4 p-4 rounded-xl bg-secondary/20 border border-border/40 space-y-2">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Question Metadata</p>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-muted-foreground block text-[10px]">Section</span>
                        <span className="font-semibold text-foreground">{activeQuestion.examCategory}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block text-[10px]">Marks</span>
                        <span className="font-semibold text-foreground">{activeQuestion.marks ?? 1} 点</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* ── Preview Empty State ───────────────────────────────── */
                <div className="flex flex-col items-center justify-center h-full py-20 gap-4 text-muted-foreground">
                  <div
                    className="w-36 h-48 rounded-sm border-2 border-stone-200 bg-white shadow-sm flex flex-col items-center justify-center gap-3 relative overflow-hidden"

                  >
                    <div className="absolute inset-[4px] border border-stone-100 opacity-50" />
                    <div className="space-y-2 w-full px-5">
                      {[70, 90, 60, 80].map((w, i) => (
                        <div key={i} className="h-1.5 bg-stone-100 rounded-full" style={{ width: `${w}%` }} />
                      ))}
                    </div>
                    <div className="space-y-1.5 w-full px-5">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="h-6 rounded-sm border border-stone-100 bg-stone-50/60" />
                      ))}
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-semibold text-foreground">CBT Exam Preview</p>
                    <p className="text-[10px] mt-1 max-w-[180px] mx-auto">
                      Select a question or scenario to see the live exam simulation
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Delete Confirmation Dialog ────────────────────────────────────────── */}
      <Dialog open={deleteConfirmId !== null} onOpenChange={(open) => { if (!open) setDeleteConfirmId(null); }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <AlertCircle size={18} />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this {deleteConfirmId?.startsWith("sc_") ? "scenario" : "question"}?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 gap-2 sm:justify-end">
            <Button variant="ghost" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (deleteConfirmId) {
                  if (deleteConfirmId.startsWith("sc_")) {
                    deleteScenarioMutation.mutate(deleteConfirmId.replace("sc_", ""), {
                      onSuccess: () => setDeleteConfirmId(null)
                    });
                  } else {
                    deleteQuestionMutation.mutate(deleteConfirmId, {
                      onSuccess: () => setDeleteConfirmId(null)
                    });
                  }
                }
              }}
              disabled={deleteQuestionMutation.isPending || deleteScenarioMutation.isPending}
            >
              {(deleteQuestionMutation.isPending || deleteScenarioMutation.isPending) ? (
                <Loader2 className="animate-spin size-4 mr-1.5" />
              ) : <Trash2 className="mr-1.5" size={14} />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Re-grade Dialog ────────────────────────────────────────────────── */}
      <Dialog open={showRegradeDialog} onOpenChange={(open) => { if (!regradeApplying) setShowRegradeDialog(open); }}>
        <DialogContent className="max-w-xl" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="text-amber-500 size-5" />
              Re-grade Submitted Attempts
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 mt-2">
                {regradeLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground py-6 justify-center">
                    <Loader2 className="animate-spin size-4" />
                    <span className="text-sm">Scanning all submitted attempts…</span>
                  </div>
                ) : regradePreview ? (
                  <>
                    <p className="text-sm text-foreground">
                      Scanned <strong>{regradePreview.total}</strong> submitted attempt{regradePreview.total !== 1 ? "s" : ""}.{" "}
                      <strong className={regradePreview.changed > 0 ? "text-amber-600" : "text-green-600"}>
                        {regradePreview.changed} will change.
                      </strong>
                    </p>

                    {regradePreview.changed === 0 ? (
                      <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-3 text-xs text-green-700 dark:text-green-400">
                        ✅ All attempt scores are already correct. No changes needed.
                      </div>
                    ) : (
                      <>
                        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-2.5 text-[10px] text-amber-700 dark:text-amber-400">
                          ⚠ These attempts will be re-scored using the <strong>current correct answers</strong>. This will overwrite stored scores and pass/fail results.
                        </div>
                        <div className="max-h-52 overflow-y-auto rounded-lg border border-border divide-y divide-border text-xs">
                          {regradePreview.summary.map((row) => (
                            <div key={row.attemptId} className="px-3 py-2 flex items-center gap-3 bg-background hover:bg-muted/30 transition-colors">
                              <div className="flex-1 min-w-0">
                                <span className="font-mono text-muted-foreground text-[10px]">User #{row.userId}</span>
                                <span className="ml-2 text-[10px] text-muted-foreground">Attempt #{row.attemptNumber}</span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {row.scoreChanged && (
                                  <span className="text-[10px]">
                                    <span className="text-red-500 line-through">{row.oldScore}</span>
                                    <span className="mx-1 text-muted-foreground">→</span>
                                    <span className="text-green-600 font-bold">{row.newScore}</span>
                                    <span className="text-muted-foreground ml-0.5">pts</span>
                                  </span>
                                )}
                                {row.passChanged && (
                                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${row.newIsPassed
                                    ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400"
                                    : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400"
                                    }`}>
                                    {row.oldIsPassed === null ? "?" : row.oldIsPassed ? "PASS" : "FAIL"} → {row.newIsPassed ? "PASS" : "FAIL"}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : null}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl"
              disabled={regradeApplying}
              onClick={() => setShowRegradeDialog(false)}
            >
              Cancel
            </Button>
            {regradePreview && regradePreview.changed > 0 && (
              <Button
                size="sm"
                className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white"
                disabled={regradeApplying}
                onClick={async () => {
                  if (!existingExam) return;
                  setRegradeApplying(true);
                  try {
                    const result = await regradeCourseExamAttempts(courseId, existingExam.id, false);
                    toast.success(`✅ Re-graded ${result.changed} attempt${result.changed !== 1 ? "s" : ""} successfully.`);
                    setShowRegradeDialog(false);
                    setRegradePreview(null);
                  } catch (e: any) {
                    toast.error(e?.message || "Re-grade failed");
                  } finally {
                    setRegradeApplying(false);
                  }
                }}
              >
                {regradeApplying
                  ? <Loader2 className="animate-spin size-3.5 mr-1" />
                  : <AlertCircle size={13} className="mr-1" />
                }
                Apply Re-grade ({regradePreview.changed} attempt{regradePreview.changed !== 1 ? "s" : ""})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Draft Recovery Dialog ─────────────────────────────────────────── */}
      <Dialog open={showRecoveryDialog} onOpenChange={() => { }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="text-amber-500 size-5" />
              Recover Unsaved Draft
            </DialogTitle>
            <DialogDescription className="space-y-2 mt-2">
              <p>
                We found an unsaved draft for this course exam session from{" "}
                <strong>{recoveredDraft ? new Date(recoveredDraft.timestamp).toLocaleString() : ""}</strong>.
              </p>
              <p className="text-xs text-muted-foreground bg-stone-50 p-2.5 rounded-lg border border-stone-150">
                This includes draft questions, options, passages, and exam configurations that weren't published or saved.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl border-dashed"
              onClick={handleDiscardDraft}
            >
              Discard Draft
            </Button>
            <Button
              variant="default"
              size="sm"
              className="rounded-xl bg-slate-900 text-white hover:bg-slate-800"
              onClick={handleRecoverDraft}
            >
              Recover Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
